import inventoryStatuses from '@bafkal/contracts-typescript/data/inventory-statuses.json';
import semanticFormatMap from '@bafkal/contracts-typescript/data/semantic-format-map.json';
import adFamilies from '@bafkal/contracts-typescript/data/ad-families.json';

// API Routes — paths are relative to the base URL (which includes /v1).
// Full path = API_BASE_URL + route, e.g. http://host/v1 + /inventory = http://host/v1/inventory
// This matches the OpenAPI spec paths: /v1/inventory, /v1/inventory/adunit/{id}
export const API_ROUTES = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
  },
  INVENTORY: {
    BASE: '/inventory',                          // operationId: listAdUnits
    AD_UNIT: '/inventory/adunit',                // operationId: createAdUnit
    AD_UNIT_BY_ID: (id) => `/inventory/adunit/${id}`, // operationId: getAdUnitById / updateAdUnit / deleteAdUnit
    PLACEMENT: '/inventory/placement',
    PLACEMENT_BY_ID: (id) => `/inventory/placement/${id}`,
  },
  DASHBOARD: '/dashboard',
  REPORTING: {
    SUMMARY: '/reporting/publisher/summary',
    BY_AD_UNIT: '/reporting/publisher/by-ad-unit',
    BY_DAY: '/reporting/publisher/by-day',
    BY_FORMAT: '/reporting/publisher/by-format',
    NO_FILL_REASONS: '/reporting/publisher/no-fill-reasons',
    EXPORT: '/reporting/publisher/export',
  },
  PAYMENT: '/payment',
  PUBLISHER_SETTINGS: '/publisher/settings',
  PUBLISHER_KEYS: '/publisher-keys',
};

// App Routes
export const APP_ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  INVENTORY: {
    ROOT: '/inventory',
    NEW: '/inventory/new',
    EDIT: (id) => `/inventory/${id}/edit`,
    PLACEMENTS: '/inventory/placements',
    PLACEMENT_NEW: '/inventory/placements/new',
    PLACEMENT_EDIT: (id) => `/inventory/placements/${id}/edit`,
  },
  PAYMENT: '/payment',
  SETTINGS: '/settings',
};

// Ad Status — sourced from @bafkal/contracts-typescript
// inventoryStatuses is an array: ["DRAFT", "ACTIVE", "PAUSED", "ARCHIVED"]
export const AD_STATUSES = inventoryStatuses;
export const AD_STATUS = Object.fromEntries(inventoryStatuses.map((s) => [s, s]));

// Ad Status Styles (Tailwind classes) — UI-only mapping, portal-specific
const STATUS_STYLE_MAP = {
  ACTIVE:   'bg-green-100 text-green-800',
  PAUSED:   'bg-yellow-100 text-yellow-800',
  DRAFT:    'bg-blue-100 text-blue-800',
  ARCHIVED: 'bg-gray-100 text-gray-800',
};
export const AD_STATUS_STYLES = Object.fromEntries(
  inventoryStatuses.map((s) => [s, STATUS_STYLE_MAP[s] || 'bg-gray-100 text-gray-800'])
);

// Ad Sizes — sourced from @bafkal/contracts-typescript semantic-format-map
// Each entry: { key, label, family, renderMode, dimensions: { width, height } }
export const AD_SIZES = semanticFormatMap;

// Ad Families — sourced from @bafkal/contracts-typescript ad-families.json
// Array of family strings: ["display", "video", "native"]
export const AD_FAMILIES = adFamilies;

// UI Messages
export const UI_MESSAGES = {
  AUTH: {
    LOGIN_SUCCESS: 'Logged in successfully',
    LOGIN_FAILED: 'Failed to login',
    REGISTER_SUCCESS: 'Registration successful',
    REGISTER_FAILED: 'Failed to register',
    LOGOUT_SUCCESS: 'Logged out successfully',
    SESSION_EXPIRED: 'Your session has expired. Please login again.',
    COGNITO_NOT_CONFIGURED: 'Cognito Managed Login is not configured. Please set REACT_APP_COGNITO_* environment variables.',
  },
  AD: {
    CREATE_SUCCESS: 'Ad created successfully',
    CREATE_FAILED: 'Failed to create ad',
    UPDATE_SUCCESS: 'Ad updated successfully',
    UPDATE_FAILED: 'Failed to update ad',
    DELETE_CONFIRM: 'Are you sure you want to delete this ad unit?',
    DELETE_SUCCESS: 'Ad deleted successfully',
    DELETE_FAILED: 'Failed to delete ad',
    FETCH_FAILED: 'Failed to fetch ads',
  },
  PLACEMENT: {
    FETCH_FAILED: 'Failed to fetch placements',
    CREATE_SUCCESS: 'Placement created successfully',
    CREATE_FAILED: 'Failed to create placement',
    UPDATE_SUCCESS: 'Placement updated successfully',
    UPDATE_FAILED: 'Failed to update placement',
    DELETE_CONFIRM: 'Are you sure you want to delete this placement?',
    DELETE_SUCCESS: 'Placement deleted successfully',
    DELETE_FAILED: 'Failed to delete placement',
  },
  SETTINGS: {
    FETCH_FAILED: 'Failed to load settings',
    SAVE_SUCCESS: 'Settings saved successfully',
    SAVE_FAILED: 'Failed to save settings',
  },
  API_KEYS: {
    FETCH_FAILED: 'Failed to fetch API keys',
    CREATE_SUCCESS: 'API key created successfully',
    CREATE_FAILED: 'Failed to create API key',
    REVOKE_SUCCESS: 'API key revoked successfully',
    REVOKE_FAILED: 'Failed to revoke API key',
  },
  GENERIC: {
    ERROR: 'Something went wrong',
    LOADING: 'Loading...',
    NO_DATA: 'No data available',
  },
};

// Environment
export const ENV = {
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  API_BASE_URL: process.env.REACT_APP_ADS_PUBLISHER_API_BASE_URL,
};

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  UNAUTHORIZED: 401,
};

// Amplify Hub Events
export const AMPLIFY_HUB_EVENTS = {
  SIGN_IN: 'signIn',
  SIGN_OUT: 'signOut',
  SIGNED_OUT: 'signedOut',
};

// Amplify Hub Channels
export const AMPLIFY_HUB_CHANNELS = {
  AUTH: 'auth',
  APP: 'app',
};
